"use client";

import { useRef, useState } from "react";
import { useEditor, EditorContent, Extension, JSONContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { MentionSuggestion, SlashSuggestion } from "./MentionPopup";
import { Tooltip } from "@nextui-org/react";
import { AiChatBtn, AiChatBtnActive, AiChatDeep, AiChatHomeSend, AiChatQuestion, AiChatThinkingBg } from "@/03-presenters/components/ui/icons";
import Placeholder from "@tiptap/extension-placeholder";
import Mention from "@tiptap/extension-mention";
import "./ChatInput.css";
import { cn } from "@/lib/utils";
// import { CustomMention } from "./CustomMention";

interface ChatInputProps {
  isAnswering: boolean;
  onCancelAnswering: () => void;
  onSubmit: (content: string) => void;
  isHome?: boolean;
  setEditor?: (editor: Editor) => void;
}
const handleSubmitContent = (json: JSONContent): Promise<string> => new Promise(resolve => {
  const formatContent = (json: any) => {
    const content: string[] = [];
    const traverse = (node: any) => {
      if (!node.content || !Array.isArray(node.content)) return;
      for (const item of node.content) {
        if (item.type === "mention-at") {
          const mention = JSON.stringify({
            id: item.attrs.id,
            type: item.attrs.type,
            name: item.attrs.label,
            region: item.attrs.region
          });
          content.push(`[@${mention}]`);
          continue;
        }
        if (item.type === "text") {
          content.push(item.text || "");
          continue;
        }
        if (item.content?.length) {
          traverse(item);
        }
      }
    };
    traverse(json);
    return content.join("");
  };
  const content = formatContent(json);
  resolve(content);
});
const EnterKeyHandler = Extension.create({
  name: "enterKeyHandler",
  addOptions() {
    return {
      handleSubmit: (editor: Editor) => {}
    };
  },
  addKeyboardShortcuts() {
    return {
      Enter: ({
        editor
      }) => {
        // 如果按下的是 Shift+Enter，直接放行
        if (editor.state.selection.$head.nodeBefore?.text?.endsWith("\n")) {
          return false;
        }
        if (editor && !!editor.getText().trim()) {
          this.options.handleSubmit(editor);
        }
        return true;
      }
    };
  }
});
const AtMention = Mention.extend({
  name: "mention-at",
  addAttributes() {
    return {
      ...this.parent?.(),
      type: {
        default: null,
        parseHTML: element => element.getAttribute("data-type"),
        renderHTML: attributes => {
          if (!attributes.type) {
            return {};
          }
          return {
            "data-type": attributes.type
          };
        }
      },
      region: {
        default: null,
        parseHTML: element => element.getAttribute("data-region"),
        renderHTML: attributes => {
          if (!attributes.region) {
            return {};
          }
          return {
            "data-region": attributes.region
          };
        }
      }
    };
  }
});
const SlashMention = Mention.extend({
  name: "mention-slash"
});

// 聊天输入组件
const ChatInput = ({
  isAnswering,
  onCancelAnswering,
  onSubmit,
  isHome = false,
  setEditor
}: ChatInputProps) => {
  const [inputValue, setInputValue] = useState("");
  /** 因为 editor 插件只在初始化时执行导致无法拿到 isAnswering 状态 */
  const isAnsweringRef = useRef(isAnswering);
  isAnsweringRef.current = isAnswering;
  const handleSubmit = (_editor?: Editor) => {
    if (isAnsweringRef.current) {
      onCancelAnswering();
      return;
    }
    const curEditor = _editor || editor;
    if (!curEditor || !curEditor.getText().trim()) return;
    handleSubmitContent(curEditor.getJSON()).then(content => {
      onSubmit(content);
      curEditor.commands.clearContent();
    });
  };
  const editor = useEditor({
    extensions: [StarterKit,
    // CustomMention,
    Placeholder.configure({
      placeholder: "欢迎使用出海匠AI，输入问题即可开始对话"
    }), SlashMention.configure({
      HTMLAttributes: {
        class: "mention-slash"
      },
      suggestion: SlashSuggestion
    }), AtMention.configure({
      HTMLAttributes: {
        class: "mention-at"
      },
      suggestion: MentionSuggestion
    }), EnterKeyHandler.configure({
      handleSubmit
    })],
    parseOptions: {
      preserveWhitespace: "full"
    },
    immediatelyRender: false,
    onCreate(props) {
      setEditor?.(props.editor);
    },
    onUpdate: ({
      editor
    }) => {
      const text = editor.getText();
      setInputValue(text.trim());
    }
  });
  const handleMentionButtonClick = (char: string) => {
    if (!editor) return;

    // 编辑器获得焦点
    editor.commands.focus();

    // 清除可能的选区，确保是在光标位置插入
    editor.commands.unsetAllMarks();

    // 在当前位置插入一个空格和字符
    const currentText = editor.getText();
    const selection = editor.state.selection;
    const lastChar = selection.$anchor.nodeBefore?.text?.slice(-1);

    // 如果上一个字符不是空格或者是在行首，添加空格
    if (currentText.length > 0 && lastChar && lastChar !== " ") {
      editor.commands.insertContent(" ");
    }
    editor.commands.insertContent(char);
  };
  return <>
      <div className="tiptap">
        <EditorContent editor={editor} className="h-auto max-h-52 min-h-10 rounded-lg p-3 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0" data-sentry-element="EditorContent" data-sentry-source-file="ChatInput.tsx" />
      </div>

      <div className="px-3 pt-2">
        <div className="flex items-center gap-3 pt-2">
          <Tooltip content="引用分析对象" color="foreground" closeDelay={100} data-sentry-element="Tooltip" data-sentry-source-file="ChatInput.tsx">
            <span className={cn("cursor-pointer text-xl text-[#4A50FF]", isHome && "text-[#A6A0FF]")} onClick={() => handleMentionButtonClick("@")}>
              {"@"}
            </span>
          </Tooltip>

          <Tooltip content="使用快捷提问模版" color="foreground" closeDelay={100} data-sentry-element="Tooltip" data-sentry-source-file="ChatInput.tsx">
            <span className={cn("cursor-pointer", isHome && "[&>svg>*]:!fill-[#A6A0FF]")} onClick={() => handleMentionButtonClick("/")}>
              <AiChatQuestion data-sentry-element="AiChatQuestion" data-sentry-source-file="ChatInput.tsx" />
            </span>
          </Tooltip>
        </div>

        <div className="relative flex w-full items-center justify-between gap-2 py-2">
          <div className="absolute -left-3 top-2.5 z-0">
            <AiChatThinkingBg data-sentry-element="AiChatThinkingBg" data-sentry-source-file="ChatInput.tsx" />
          </div>

          <div className={cn("mt-2 flex items-center gap-2 text-sm font-medium text-[#4A50FF]", isHome && "text-[#A6A0FF]")}>
            <AiChatDeep data-sentry-element="AiChatDeep" data-sentry-source-file="ChatInput.tsx" />
            <span>深度思考</span>
          </div>

          {isHome ? <div className="cursor-pointer" onClick={() => handleSubmit()}>
              <AiChatHomeSend />
            </div> : <>
              {isAnswering ? <div className={cn("flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-[#4A50FF]")} onClick={onCancelAnswering}>
                  <span className="h-2.5 w-2.5 bg-white" />
                </div> : <>
                  {inputValue.length ? <div className="cursor-pointer" onClick={() => handleSubmit()}>
                      <AiChatBtnActive />
                    </div> : <AiChatBtn />}
                </>}
            </>}
        </div>
      </div>
    </>;
};
ChatInput.displayName = "ChatInput";
export default ChatInput;